/**
 * Bundled phone country data — no API call, per `06-DESIGN-SYSTEM.md` §6.8's phone field spec
 * and WP-18's own requirement ("country data bundled").
 *
 * D3 (0.9.41-rc3): every region libphonenumber's metadata knows (245), so each country's number is
 * formatted and limited to its own length: the United States and Canada first, then the rest by
 * name. The codes and dial codes are libphonenumber-js 1.13.15's own (`getCountries()`,
 * `getCountryCallingCode()`), the names the standard English region names (`Intl.DisplayNames`);
 * `tests/Browser/phone-field.spec.js` checks the list against the metadata the form loads.
 *
 * `flag` is the country's Unicode regional-indicator emoji, which every browser this plugin
 * targets renders without an image request — the same "no API call, no external asset" property
 * the dial code itself has.
 *
 * @type {ReadonlyArray<{iso2: string, name: string, dial: string, flag: string}>}
 */
export const COUNTRIES = Object.freeze(
	[
		{ iso2: 'US', name: 'United States', dial: '1' },
		{ iso2: 'CA', name: 'Canada', dial: '1' },
		{ iso2: 'AF', name: 'Afghanistan', dial: '93' },
		{ iso2: 'AX', name: 'Åland Islands', dial: '358' },
		{ iso2: 'AL', name: 'Albania', dial: '355' },
		{ iso2: 'DZ', name: 'Algeria', dial: '213' },
		{ iso2: 'AS', name: 'American Samoa', dial: '1' },
		{ iso2: 'AD', name: 'Andorra', dial: '376' },
		{ iso2: 'AO', name: 'Angola', dial: '244' },
		{ iso2: 'AI', name: 'Anguilla', dial: '1' },
		{ iso2: 'AG', name: 'Antigua & Barbuda', dial: '1' },
		{ iso2: 'AR', name: 'Argentina', dial: '54' },
		{ iso2: 'AM', name: 'Armenia', dial: '374' },
		{ iso2: 'AW', name: 'Aruba', dial: '297' },
		{ iso2: 'AC', name: 'Ascension Island', dial: '247' },
		{ iso2: 'AU', name: 'Australia', dial: '61' },
		{ iso2: 'AT', name: 'Austria', dial: '43' },
		{ iso2: 'AZ', name: 'Azerbaijan', dial: '994' },
		{ iso2: 'BS', name: 'Bahamas', dial: '1' },
		{ iso2: 'BH', name: 'Bahrain', dial: '973' },
		{ iso2: 'BD', name: 'Bangladesh', dial: '880' },
		{ iso2: 'BB', name: 'Barbados', dial: '1' },
		{ iso2: 'BY', name: 'Belarus', dial: '375' },
		{ iso2: 'BE', name: 'Belgium', dial: '32' },
		{ iso2: 'BZ', name: 'Belize', dial: '501' },
		{ iso2: 'BJ', name: 'Benin', dial: '229' },
		{ iso2: 'BM', name: 'Bermuda', dial: '1' },
		{ iso2: 'BT', name: 'Bhutan', dial: '975' },
		{ iso2: 'BO', name: 'Bolivia', dial: '591' },
		{ iso2: 'BA', name: 'Bosnia & Herzegovina', dial: '387' },
		{ iso2: 'BW', name: 'Botswana', dial: '267' },
		{ iso2: 'BR', name: 'Brazil', dial: '55' },
		{ iso2: 'IO', name: 'British Indian Ocean Territory', dial: '246' },
		{ iso2: 'VG', name: 'British Virgin Islands', dial: '1' },
		{ iso2: 'BN', name: 'Brunei', dial: '673' },
		{ iso2: 'BG', name: 'Bulgaria', dial: '359' },
		{ iso2: 'BF', name: 'Burkina Faso', dial: '226' },
		{ iso2: 'BI', name: 'Burundi', dial: '257' },
		{ iso2: 'KH', name: 'Cambodia', dial: '855' },
		{ iso2: 'CM', name: 'Cameroon', dial: '237' },
		{ iso2: 'CV', name: 'Cape Verde', dial: '238' },
		{ iso2: 'BQ', name: 'Caribbean Netherlands', dial: '599' },
		{ iso2: 'KY', name: 'Cayman Islands', dial: '1' },
		{ iso2: 'CF', name: 'Central African Republic', dial: '236' },
		{ iso2: 'TD', name: 'Chad', dial: '235' },
		{ iso2: 'CL', name: 'Chile', dial: '56' },
		{ iso2: 'CN', name: 'China', dial: '86' },
		{ iso2: 'CX', name: 'Christmas Island', dial: '61' },
		{ iso2: 'CC', name: 'Cocos (Keeling) Islands', dial: '61' },
		{ iso2: 'CO', name: 'Colombia', dial: '57' },
		{ iso2: 'KM', name: 'Comoros', dial: '269' },
		{ iso2: 'CG', name: 'Congo - Brazzaville', dial: '242' },
		{ iso2: 'CD', name: 'Congo - Kinshasa', dial: '243' },
		{ iso2: 'CK', name: 'Cook Islands', dial: '682' },
		{ iso2: 'CR', name: 'Costa Rica', dial: '506' },
		{ iso2: 'CI', name: 'Côte d’Ivoire', dial: '225' },
		{ iso2: 'HR', name: 'Croatia', dial: '385' },
		{ iso2: 'CU', name: 'Cuba', dial: '53' },
		{ iso2: 'CW', name: 'Curaçao', dial: '599' },
		{ iso2: 'CY', name: 'Cyprus', dial: '357' },
		{ iso2: 'CZ', name: 'Czechia', dial: '420' },
		{ iso2: 'DK', name: 'Denmark', dial: '45' },
		{ iso2: 'DJ', name: 'Djibouti', dial: '253' },
		{ iso2: 'DM', name: 'Dominica', dial: '1' },
		{ iso2: 'DO', name: 'Dominican Republic', dial: '1' },
		{ iso2: 'EC', name: 'Ecuador', dial: '593' },
		{ iso2: 'EG', name: 'Egypt', dial: '20' },
		{ iso2: 'SV', name: 'El Salvador', dial: '503' },
		{ iso2: 'GQ', name: 'Equatorial Guinea', dial: '240' },
		{ iso2: 'ER', name: 'Eritrea', dial: '291' },
		{ iso2: 'EE', name: 'Estonia', dial: '372' },
		{ iso2: 'SZ', name: 'Eswatini', dial: '268' },
		{ iso2: 'ET', name: 'Ethiopia', dial: '251' },
		{ iso2: 'FK', name: 'Falkland Islands', dial: '500' },
		{ iso2: 'FO', name: 'Faroe Islands', dial: '298' },
		{ iso2: 'FJ', name: 'Fiji', dial: '679' },
		{ iso2: 'FI', name: 'Finland', dial: '358' },
		{ iso2: 'FR', name: 'France', dial: '33' },
		{ iso2: 'GF', name: 'French Guiana', dial: '594' },
		{ iso2: 'PF', name: 'French Polynesia', dial: '689' },
		{ iso2: 'GA', name: 'Gabon', dial: '241' },
		{ iso2: 'GM', name: 'Gambia', dial: '220' },
		{ iso2: 'GE', name: 'Georgia', dial: '995' },
		{ iso2: 'DE', name: 'Germany', dial: '49' },
		{ iso2: 'GH', name: 'Ghana', dial: '233' },
		{ iso2: 'GI', name: 'Gibraltar', dial: '350' },
		{ iso2: 'GR', name: 'Greece', dial: '30' },
		{ iso2: 'GL', name: 'Greenland', dial: '299' },
		{ iso2: 'GD', name: 'Grenada', dial: '1' },
		{ iso2: 'GP', name: 'Guadeloupe', dial: '590' },
		{ iso2: 'GU', name: 'Guam', dial: '1' },
		{ iso2: 'GT', name: 'Guatemala', dial: '502' },
		{ iso2: 'GG', name: 'Guernsey', dial: '44' },
		{ iso2: 'GN', name: 'Guinea', dial: '224' },
		{ iso2: 'GW', name: 'Guinea-Bissau', dial: '245' },
		{ iso2: 'GY', name: 'Guyana', dial: '592' },
		{ iso2: 'HT', name: 'Haiti', dial: '509' },
		{ iso2: 'HN', name: 'Honduras', dial: '504' },
		{ iso2: 'HK', name: 'Hong Kong SAR China', dial: '852' },
		{ iso2: 'HU', name: 'Hungary', dial: '36' },
		{ iso2: 'IS', name: 'Iceland', dial: '354' },
		{ iso2: 'IN', name: 'India', dial: '91' },
		{ iso2: 'ID', name: 'Indonesia', dial: '62' },
		{ iso2: 'IR', name: 'Iran', dial: '98' },
		{ iso2: 'IQ', name: 'Iraq', dial: '964' },
		{ iso2: 'IE', name: 'Ireland', dial: '353' },
		{ iso2: 'IM', name: 'Isle of Man', dial: '44' },
		{ iso2: 'IL', name: 'Israel', dial: '972' },
		{ iso2: 'IT', name: 'Italy', dial: '39' },
		{ iso2: 'JM', name: 'Jamaica', dial: '1' },
		{ iso2: 'JP', name: 'Japan', dial: '81' },
		{ iso2: 'JE', name: 'Jersey', dial: '44' },
		{ iso2: 'JO', name: 'Jordan', dial: '962' },
		{ iso2: 'KZ', name: 'Kazakhstan', dial: '7' },
		{ iso2: 'KE', name: 'Kenya', dial: '254' },
		{ iso2: 'KI', name: 'Kiribati', dial: '686' },
		{ iso2: 'XK', name: 'Kosovo', dial: '383' },
		{ iso2: 'KW', name: 'Kuwait', dial: '965' },
		{ iso2: 'KG', name: 'Kyrgyzstan', dial: '996' },
		{ iso2: 'LA', name: 'Laos', dial: '856' },
		{ iso2: 'LV', name: 'Latvia', dial: '371' },
		{ iso2: 'LB', name: 'Lebanon', dial: '961' },
		{ iso2: 'LS', name: 'Lesotho', dial: '266' },
		{ iso2: 'LR', name: 'Liberia', dial: '231' },
		{ iso2: 'LY', name: 'Libya', dial: '218' },
		{ iso2: 'LI', name: 'Liechtenstein', dial: '423' },
		{ iso2: 'LT', name: 'Lithuania', dial: '370' },
		{ iso2: 'LU', name: 'Luxembourg', dial: '352' },
		{ iso2: 'MO', name: 'Macao SAR China', dial: '853' },
		{ iso2: 'MG', name: 'Madagascar', dial: '261' },
		{ iso2: 'MW', name: 'Malawi', dial: '265' },
		{ iso2: 'MY', name: 'Malaysia', dial: '60' },
		{ iso2: 'MV', name: 'Maldives', dial: '960' },
		{ iso2: 'ML', name: 'Mali', dial: '223' },
		{ iso2: 'MT', name: 'Malta', dial: '356' },
		{ iso2: 'MH', name: 'Marshall Islands', dial: '692' },
		{ iso2: 'MQ', name: 'Martinique', dial: '596' },
		{ iso2: 'MR', name: 'Mauritania', dial: '222' },
		{ iso2: 'MU', name: 'Mauritius', dial: '230' },
		{ iso2: 'YT', name: 'Mayotte', dial: '262' },
		{ iso2: 'MX', name: 'Mexico', dial: '52' },
		{ iso2: 'FM', name: 'Micronesia', dial: '691' },
		{ iso2: 'MD', name: 'Moldova', dial: '373' },
		{ iso2: 'MC', name: 'Monaco', dial: '377' },
		{ iso2: 'MN', name: 'Mongolia', dial: '976' },
		{ iso2: 'ME', name: 'Montenegro', dial: '382' },
		{ iso2: 'MS', name: 'Montserrat', dial: '1' },
		{ iso2: 'MA', name: 'Morocco', dial: '212' },
		{ iso2: 'MZ', name: 'Mozambique', dial: '258' },
		{ iso2: 'MM', name: 'Myanmar (Burma)', dial: '95' },
		{ iso2: 'NA', name: 'Namibia', dial: '264' },
		{ iso2: 'NR', name: 'Nauru', dial: '674' },
		{ iso2: 'NP', name: 'Nepal', dial: '977' },
		{ iso2: 'NL', name: 'Netherlands', dial: '31' },
		{ iso2: 'NC', name: 'New Caledonia', dial: '687' },
		{ iso2: 'NZ', name: 'New Zealand', dial: '64' },
		{ iso2: 'NI', name: 'Nicaragua', dial: '505' },
		{ iso2: 'NE', name: 'Niger', dial: '227' },
		{ iso2: 'NG', name: 'Nigeria', dial: '234' },
		{ iso2: 'NU', name: 'Niue', dial: '683' },
		{ iso2: 'NF', name: 'Norfolk Island', dial: '672' },
		{ iso2: 'KP', name: 'North Korea', dial: '850' },
		{ iso2: 'MK', name: 'North Macedonia', dial: '389' },
		{ iso2: 'MP', name: 'Northern Mariana Islands', dial: '1' },
		{ iso2: 'NO', name: 'Norway', dial: '47' },
		{ iso2: 'OM', name: 'Oman', dial: '968' },
		{ iso2: 'PK', name: 'Pakistan', dial: '92' },
		{ iso2: 'PW', name: 'Palau', dial: '680' },
		{ iso2: 'PS', name: 'Palestinian Territories', dial: '970' },
		{ iso2: 'PA', name: 'Panama', dial: '507' },
		{ iso2: 'PG', name: 'Papua New Guinea', dial: '675' },
		{ iso2: 'PY', name: 'Paraguay', dial: '595' },
		{ iso2: 'PE', name: 'Peru', dial: '51' },
		{ iso2: 'PH', name: 'Philippines', dial: '63' },
		{ iso2: 'PL', name: 'Poland', dial: '48' },
		{ iso2: 'PT', name: 'Portugal', dial: '351' },
		{ iso2: 'PR', name: 'Puerto Rico', dial: '1' },
		{ iso2: 'QA', name: 'Qatar', dial: '974' },
		{ iso2: 'RE', name: 'Réunion', dial: '262' },
		{ iso2: 'RO', name: 'Romania', dial: '40' },
		{ iso2: 'RU', name: 'Russia', dial: '7' },
		{ iso2: 'RW', name: 'Rwanda', dial: '250' },
		{ iso2: 'WS', name: 'Samoa', dial: '685' },
		{ iso2: 'SM', name: 'San Marino', dial: '378' },
		{ iso2: 'ST', name: 'São Tomé & Príncipe', dial: '239' },
		{ iso2: 'SA', name: 'Saudi Arabia', dial: '966' },
		{ iso2: 'SN', name: 'Senegal', dial: '221' },
		{ iso2: 'RS', name: 'Serbia', dial: '381' },
		{ iso2: 'SC', name: 'Seychelles', dial: '248' },
		{ iso2: 'SL', name: 'Sierra Leone', dial: '232' },
		{ iso2: 'SG', name: 'Singapore', dial: '65' },
		{ iso2: 'SX', name: 'Sint Maarten', dial: '1' },
		{ iso2: 'SK', name: 'Slovakia', dial: '421' },
		{ iso2: 'SI', name: 'Slovenia', dial: '386' },
		{ iso2: 'SB', name: 'Solomon Islands', dial: '677' },
		{ iso2: 'SO', name: 'Somalia', dial: '252' },
		{ iso2: 'ZA', name: 'South Africa', dial: '27' },
		{ iso2: 'KR', name: 'South Korea', dial: '82' },
		{ iso2: 'SS', name: 'South Sudan', dial: '211' },
		{ iso2: 'ES', name: 'Spain', dial: '34' },
		{ iso2: 'LK', name: 'Sri Lanka', dial: '94' },
		{ iso2: 'BL', name: 'St. Barthélemy', dial: '590' },
		{ iso2: 'SH', name: 'St. Helena', dial: '290' },
		{ iso2: 'KN', name: 'St. Kitts & Nevis', dial: '1' },
		{ iso2: 'LC', name: 'St. Lucia', dial: '1' },
		{ iso2: 'MF', name: 'St. Martin', dial: '590' },
		{ iso2: 'PM', name: 'St. Pierre & Miquelon', dial: '508' },
		{ iso2: 'VC', name: 'St. Vincent & Grenadines', dial: '1' },
		{ iso2: 'SD', name: 'Sudan', dial: '249' },
		{ iso2: 'SR', name: 'Suriname', dial: '597' },
		{ iso2: 'SJ', name: 'Svalbard & Jan Mayen', dial: '47' },
		{ iso2: 'SE', name: 'Sweden', dial: '46' },
		{ iso2: 'CH', name: 'Switzerland', dial: '41' },
		{ iso2: 'SY', name: 'Syria', dial: '963' },
		{ iso2: 'TW', name: 'Taiwan', dial: '886' },
		{ iso2: 'TJ', name: 'Tajikistan', dial: '992' },
		{ iso2: 'TZ', name: 'Tanzania', dial: '255' },
		{ iso2: 'TH', name: 'Thailand', dial: '66' },
		{ iso2: 'TL', name: 'Timor-Leste', dial: '670' },
		{ iso2: 'TG', name: 'Togo', dial: '228' },
		{ iso2: 'TK', name: 'Tokelau', dial: '690' },
		{ iso2: 'TO', name: 'Tonga', dial: '676' },
		{ iso2: 'TT', name: 'Trinidad & Tobago', dial: '1' },
		{ iso2: 'TA', name: 'Tristan da Cunha', dial: '290' },
		{ iso2: 'TN', name: 'Tunisia', dial: '216' },
		{ iso2: 'TR', name: 'Türkiye', dial: '90' },
		{ iso2: 'TM', name: 'Turkmenistan', dial: '993' },
		{ iso2: 'TC', name: 'Turks & Caicos Islands', dial: '1' },
		{ iso2: 'TV', name: 'Tuvalu', dial: '688' },
		{ iso2: 'VI', name: 'U.S. Virgin Islands', dial: '1' },
		{ iso2: 'UG', name: 'Uganda', dial: '256' },
		{ iso2: 'UA', name: 'Ukraine', dial: '380' },
		{ iso2: 'AE', name: 'United Arab Emirates', dial: '971' },
		{ iso2: 'GB', name: 'United Kingdom', dial: '44' },
		{ iso2: 'UY', name: 'Uruguay', dial: '598' },
		{ iso2: 'UZ', name: 'Uzbekistan', dial: '998' },
		{ iso2: 'VU', name: 'Vanuatu', dial: '678' },
		{ iso2: 'VA', name: 'Vatican City', dial: '39' },
		{ iso2: 'VE', name: 'Venezuela', dial: '58' },
		{ iso2: 'VN', name: 'Vietnam', dial: '84' },
		{ iso2: 'WF', name: 'Wallis & Futuna', dial: '681' },
		{ iso2: 'EH', name: 'Western Sahara', dial: '212' },
		{ iso2: 'YE', name: 'Yemen', dial: '967' },
		{ iso2: 'ZM', name: 'Zambia', dial: '260' },
		{ iso2: 'ZW', name: 'Zimbabwe', dial: '263' },
	].map( ( country ) => ( { ...country, flag: dialCodeFlag( country.iso2 ) } ) )
);

/**
 * Converts an ISO-3166 alpha-2 code into its Unicode regional-indicator flag emoji.
 *
 * @param {string} iso2
 * @return {string}
 */
function dialCodeFlag( iso2 ) {
	const codePoints = iso2
		.toUpperCase()
		.split( '' )
		.map( ( letter ) => 127397 + letter.charCodeAt( 0 ) );

	return String.fromCodePoint( ...codePoints );
}

/**
 * Returns the countries whose name or dial code matches a search term, case-insensitively.
 *
 * @param {string} term
 * @return {ReadonlyArray<{iso2: string, name: string, dial: string, flag: string}>}
 */
export function searchCountries( term ) {
	const needle = term.trim().toLowerCase();

	if ( '' === needle ) {
		return COUNTRIES;
	}

	return COUNTRIES.filter(
		( country ) =>
			country.name.toLowerCase().includes( needle ) || country.dial.includes( needle.replace( '+', '' ) )
	);
}

/**
 * Returns one country by its ISO-3166 alpha-2 code, or the first (United States) as a default.
 *
 * @param {string} iso2
 * @return {{iso2: string, name: string, dial: string, flag: string}}
 */
export function countryByIso2( iso2 ) {
	return COUNTRIES.find( ( country ) => country.iso2 === iso2 ) || COUNTRIES[ 0 ];
}
